import React, { useEffect, useState } from 'react'
import {
  MenuItem,
  Box,
  IconButton,
  Tooltip,
  Grid,
  Typography,
  Toolbar,
  TextField,
  Select,
  OutlinedInput,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  Autocomplete,
} from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import SaveIcon from '@mui/icons-material/Save'
import AddCircleIcon from '@mui/icons-material/AddCircle'
import EditIcon from '@mui/icons-material/Edit'
import { useQuery } from '@apollo/react-hooks'
import { GET_CATEGORIES } from './gql'
import { VoucherType, VoucherCostCategory, CostCategory } from '../../types'
import Receipts from './Receipts'

// Types for the component props and callbacks
export interface CostCategoriesProps {
  voucher: VoucherType
  readonly?: boolean
  onAdd?: (costCategory: Omit<VoucherCostCategory, 'id'>) => Promise<void>
  onUpdate?: (
    id: string,
    costCategory: Partial<VoucherCostCategory>
  ) => Promise<void>
  onDelete?: (id: string) => Promise<void>
}

// Types for internal state
interface EditingState {
  id: string | null
  cost_category_id: string
  cost_share: number
}

const CostCategories: React.FC<CostCategoriesProps> = ({
  voucher,
  readonly = false,
  onAdd,
  onUpdate,
  onDelete,
}) => {
  // Fetch categories for dropdowns
  const {
    data: categoriesData,
    loading: categoriesLoading,
    error: categoriesError,
  } = useQuery(GET_CATEGORIES)
  const categories: CostCategory[] = categoriesData?.costCategories || []

  // State for editing mode
  const [editingState, setEditingState] = useState<EditingState | null>(null)
  const [isAdding, setIsAdding] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Reset editing state when voucher changes
  useEffect(() => {
    setEditingState(null)
    setIsAdding(false)
    setError(null)
  }, [voucher.id])

  // Handle adding new cost category
  const handleAddCostCategory = () => {
    setIsAdding(true)
    setEditingState({
      id: null,
      cost_category_id: '',
      cost_share: 0,
    })
    setError(null)
  }

  // Handle editing existing cost category
  const handleEditCostCategory = (costCategory: VoucherCostCategory) => {
    setEditingState({
      id: costCategory.id?.toString() || '',
      cost_category_id: costCategory.cost_category.id,
      cost_share: costCategory.cost_share,
    })
    setIsAdding(false)
    setError(null)
  }

  // Handle canceling edit/add mode
  const handleCancelEdit = () => {
    setEditingState(null)
    setIsAdding(false)
    setError(null)
  }

  // Handle saving cost category (add or update)
  const handleSaveCostCategory = async () => {
    if (!editingState) {
      console.log('No editing state, returning')
      return
    }

    const { cost_category_id, cost_share } = editingState

    if (!cost_category_id) {
      console.log('Validation failed - missing category')
      setError('Please select a category.')
      return
    }

    if (cost_share <= 0) {
      console.log('Validation failed - invalid cost share')
      setError('Please enter a valid cost share amount greater than 0.')
      return
    }

    // Check if the selected category is a valid child category (not a parent category)
    if (cost_category_id.startsWith('parent-')) {
      console.log('Validation failed - parent category selected')
      setError('Please select a child category, not a parent category.')
      return
    }

    try {
      setError(null)
      if (isAdding && onAdd) {
        const selectedCategory = categories.find(
          (c) => c.id === cost_category_id
        )
        if (!selectedCategory) {
          throw new Error('Selected category not found')
        }

        await onAdd({
          cost_category: selectedCategory,
          cost_share,
        })
      } else if (editingState.id && onUpdate) {
        const selectedCategory = categories.find(
          (c) => c.id === cost_category_id
        )
        if (!selectedCategory) {
          throw new Error('Selected category not found')
        }
        await onUpdate(editingState.id, {
          cost_category: selectedCategory,
          cost_share,
        })
      }

      // Reset state on success
      setEditingState(null)
      setIsAdding(false)
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to save cost category'
      )
    }
  }

  // Handle deleting cost category
  const handleDeleteCostCategory = async (id: string) => {
    if (!onDelete) return

    if (window.confirm('Are you sure you want to delete this cost category?')) {
      try {
        setError(null)
        await onDelete(id)
      } catch (err) {
        setError(
          err instanceof Error ? err.message : 'Failed to delete cost category'
        )
      }
    }
  }

  // Handle editing state changes
  const handleEditingStateChange = (
    field: keyof EditingState,
    value: string | number
  ) => {
    if (!editingState) return

    setEditingState((prev) =>
      prev
        ? {
            ...prev,
            [field]: value,
          }
        : null
    )
  }

  // Get parent categories (categories without parent_id)
  const parentCategories = categories.filter((c) => !c.parent_id)

  // Get child categories for a given parent
  const getChildCategories = (parentId: string) => {
    const childCategories = categories.filter((c) => c.parent_id === parentId)
    return childCategories
  }

  // Find the parent category of a given category
  const findParentCategory = (categoryId: string) => {
    const category = categories.find((c) => c.id === categoryId)
    if (!category?.parent_id) return null
    return categories.find((c) => c.id === category.parent_id)
  }

  if (categoriesLoading) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography>Loading categories...</Typography>
      </Box>
    )
  }

  if (categoriesError) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">
          Failed to load categories: {categoriesError.message}
        </Alert>
      </Box>
    )
  }

  return (
    <Box
      sx={{
        border: 1,
        py: 2,
        px: { xs: 1, sm: 2 },
        mb: 2,
        borderRadius: 1,
        borderColor: 'secondary.main',
        backgroundColor: 'background.paper',
      }}
    >
      <Toolbar variant="dense" disableGutters>
        <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
          Cost Categories
        </Typography>
        {!readonly && (
          <IconButton
            onClick={handleAddCostCategory}
            title="Add New Cost Category"
            disabled={isAdding || editingState !== null}
          >
            <AddCircleIcon fontSize="medium" color="success" />
          </IconButton>
        )}
      </Toolbar>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Add/Edit Form */}
      {(isAdding || editingState) && (
        <Box
          sx={{
            mb: 3,
            p: 2,
            border: 1,
            borderColor: 'primary.main',
            borderRadius: 1,
          }}
        >
          <Typography variant="subtitle2" sx={{ mb: 2 }}>
            {isAdding ? 'Add New Cost Category' : 'Edit Cost Category'}
          </Typography>

          <Grid container spacing={2} alignItems="center">
            {/* Combined Category Dropdown */}
            <Grid item xs={12} sm={6} md={8}>
              <Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mb: 0.5, display: 'block' }}
                >
                  Cost Category
                </Typography>
                <Autocomplete
                  value={
                    categories.find(
                      (c) => c.id === (editingState?.cost_category_id || '')
                    ) || null
                  }
                  options={categories.filter((c) => c.parent_id)}
                  groupBy={(option: CostCategory) =>
                    findParentCategory(option.id)?.name || 'Other'
                  }
                  getOptionLabel={(option: CostCategory) =>
                    `${option.name} (${option.code})`
                  }
                  onChange={(event: any, newValue: CostCategory | null) => {
                    handleEditingStateChange(
                      'cost_category_id',
                      newValue ? newValue.id : ''
                    )
                  }}
                  disabled={readonly}
                  renderInput={(params: any) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      size="small" // Make it small to match other inputs
                      placeholder="Select Cost Category"
                    />
                  )}
                  renderOption={(props: any, option: CostCategory) => (
                    <li {...props} key={option.id}>
                      {option.name} ({option.code})
                    </li>
                  )}
                  isOptionEqualToValue={(
                    option: CostCategory,
                    value: CostCategory
                  ) => option.id === value.id}
                />
              </Box>
            </Grid>

            {/* Cost Share */}
            <Grid item xs={12} sm={6} md={3}>
              <Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mb: 0.5, display: 'block' }}
                >
                  Cost Share
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  type="number"
                  value={editingState?.cost_share || ''}
                  variant="outlined"
                  disabled={readonly}
                  onChange={(e) =>
                    handleEditingStateChange(
                      'cost_share',
                      parseFloat(e.target.value) || 0
                    )
                  }
                  inputProps={{ min: 0, step: 0.01 }}
                  placeholder="Enter amount"
                />
              </Box>
            </Grid>

            {/* Action Buttons */}
            <Grid item xs={12} sm={6} md={1}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                <Tooltip title="Save">
                  <IconButton
                    onClick={handleSaveCostCategory}
                    sx={{
                      backgroundColor: 'primary.light',
                      color: 'white',
                      '&:hover': {
                        backgroundColor: 'primary.main',
                      },
                    }}
                  >
                    <SaveIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Cancel">
                  <IconButton
                    onClick={handleCancelEdit}
                    sx={{
                      backgroundColor: 'grey.400',
                      color: 'white',
                      '&:hover': {
                        backgroundColor: 'grey.500',
                      },
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            </Grid>
          </Grid>
        </Box>
      )}

      {/* Existing Cost Categories List */}
      {voucher.voucher_cost_category.map((costCategory) => (
        <Box
          key={costCategory.id}
          sx={{
            mb: 2,
            p: 2,
            border: 1,
            borderColor: 'grey.300',
            borderRadius: 1,
          }}
        >
          <Grid container spacing={2} alignItems="center">
            {/* Combined Category Display */}
            <Grid item xs={12} sm={6} md={8}>
              <Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mb: 0.5, display: 'block' }}
                >
                  Cost Category
                </Typography>
                <Typography variant="body2">
                  {findParentCategory(costCategory.cost_category.id)?.name && (
                    <span
                      style={{ fontWeight: 'bold', color: 'text.secondary' }}
                    >
                      {findParentCategory(costCategory.cost_category.id)?.name}{' '}
                      →{' '}
                    </span>
                  )}
                  {costCategory.cost_category.name} ({costCategory.cost_category.code})
                </Typography>
              </Box>
            </Grid>

            {/* Cost Share Display */}
            <Grid item xs={12} sm={6} md={3}>
              <Box>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mb: 0.5, display: 'block' }}
                >
                  Cost Share
                </Typography>
                <Typography variant="body2">
                  {voucher.currency} {costCategory.cost_share.toFixed(2)}
                </Typography>
              </Box>
            </Grid>

            {/* Action Buttons */}
            {!readonly && (
              <Grid item xs={12} sm={6} md={1}>
                <Box
                  sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}
                >
                  <Tooltip title="Edit Cost Category">
                    <IconButton
                      onClick={() => handleEditCostCategory(costCategory)}
                      disabled={editingState !== null}
                      sx={{
                        backgroundColor: 'info.light',
                        color: 'white',
                        '&:hover': {
                          backgroundColor: 'info.main',
                        },
                      }}
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete Cost Category">
                    <IconButton
                      onClick={() =>
                        handleDeleteCostCategory(
                          costCategory.id?.toString() || ''
                        )
                      }
                      disabled={editingState !== null}
                      sx={{
                        backgroundColor: 'error.light',
                        color: 'white',
                        '&:hover': {
                          backgroundColor: 'error.main',
                        },
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Grid>
            )}
          </Grid>

          {/* Receipts Section */}
          <Box sx={{ mt: 2 }}>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableBody>
                  <Receipts
                    voucherId={voucher.id}
                    voucherCostCategory={costCategory}
                    onCategoryChange={(
                      categoryId: string | number,
                      updatedCategory: VoucherCostCategory
                    ) => {
                      // Update the voucher state when receipts change
                      if (onUpdate) {
                        onUpdate(categoryId.toString(), updatedCategory)
                      }
                    }}
                    voucherReceipts={costCategory.receipts || []}
                    paidByAmin={voucher.paid_by_amin}
                    readonly={readonly}
                  />
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Box>
      ))}

      {/* Empty State */}
      {voucher.voucher_cost_category.length === 0 && !isAdding && (
        <Box sx={{ p: 2, textAlign: 'center' }}>
          <Typography color="text.secondary">
            No cost categories added yet.
            {!readonly && ' Click the + button to add one.'}
          </Typography>
        </Box>
      )}
    </Box>
  )
}

export default CostCategories
